import { call } from "./tasks";
import { tx } from "../i18n";

/* ══ 종 · 알람 화면이 읽는 문 — `crates/api/src/alarm.rs` ══
   시안 `t-in-alarm-design` 채택안 A(Figma 58-3) · 공통 프레임 60-2 ④ 가 요구한 것만 있다.
   알람은 서버가 **읽을 때** 만든다 — 화면은 갈래·톤·갈 곳을 다시 판정하지 않고 받은 값을 그린다. */

/** 갈래. **모르는 값이 올 수 있다**(서버가 갈래를 늘린 날) — 그래서 string 이고, 화면은 모르면
 *  코드 그대로 그린다. */
export type AlarmKind = "blocked" | "rework" | "session-failed" | "stale" | "review" | "runner-down";
export type AlarmTone = "alert" | "warn" | "info";
/** 누르면 어디로 가나 — 서버가 정한다(`alarm::go_of`). */
export type AlarmGo = "task" | "session" | "tidy" | "servers" | "alarms";

export type Alarm = {
  id: string;
  kind: string;
  tone: string;
  /** 이슈가 지워졌으면 null — 그때 갈 곳은 알람 화면이다. */
  taskId: string | null;
  /** 쓸 때 박은 제목. 이슈가 지워져도 줄이 말한다. */
  taskTitle: string;
  sessionId: string | null;
  body: string;
  go: string;
  createdAt: string;
  read: boolean;
  /** 러너 끊김이면 그 러너 이름(제목과 같다). 이슈 갈래는 null. */
  runner?: string | null;
  /** 끝난 사실(러너가 다시 붙음)이면 그 시각 — 줄은 남고 흐려진다. 서버가 정한다. */
  resolvedAt?: string | null;
};

export type AlarmPage = {
  items: Alarm[];
  /** 다음 쪽 커서. **서버 값 그대로** 보낸다 — 화면이 시각으로 만들지 않는다. */
  nextBefore: string | null;
  /** 거르개에 맞는 전체 수 — 「120건 중 30건」. */
  total: number;
};

/** 종의 숫자. **목록을 세지 않는다** — 목록은 종을 열 때만 30줄 받는다. */
export type Unread = { unread: number; alert: number };

/** `since` 는 RFC3339 시각(그 뒤에 생긴 것만), `task` 는 대상 이슈 id 하나 — 둘 다 **서버 거르개**다(쪽으로 오니까). */
export type AlarmQuery = { before?: string | null; limit?: number; kinds?: string[]; unread?: boolean; since?: string | null; task?: string | null };

function qs(q: AlarmQuery): string {
  const p = new URLSearchParams();
  if (q.before) p.set("before", q.before);
  if (q.limit) p.set("limit", String(q.limit));
  if (q.kinds && q.kinds.length) p.set("kind", q.kinds.join(","));
  if (q.unread) p.set("unread", "true");
  if (q.since) p.set("since", q.since);
  if (q.task) p.set("task", q.task);
  const s = p.toString();
  return s ? `?${s}` : "";
}

const post = <T>(path: string, body: unknown) =>
  call<T>(path, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(body) });

export const alarmApi = {
  unread: () => call<Unread>(`/alarms/unread`),
  list: (q: AlarmQuery = {}) => call<AlarmPage>(`/alarms${qs(q)}`),
  read: (ids: string[]) => post<{ marked: number }>(`/alarms/read`, { ids }),
  readAll: () => post<{ marked: number }>(`/alarms/read`, { all: true }),
};

/** 기간 — 「오늘」은 KST 자정부터(dbix 와 같은 시간대), 나머지는 지금부터 거꾸로 센 날수다. */
export type AlarmRange = "today" | "7d" | "30d" | "all";
export const RANGE_KEYS: AlarmRange[] = ["today", "7d", "30d", "all"];
const KST = 9 * 3600_000;
export function sinceOf(r: AlarmRange, now: number): string | null {
  if (r === "all") return null;
  if (r === "today") return new Date(Math.floor((now + KST) / 86_400_000) * 86_400_000 - KST).toISOString();
  const days = r === "7d" ? 7 : 30;
  return new Date(now - days * 86_400_000).toISOString();
}

/** 갈래 표 — 글자. 톤(글리프·색)은 줄이 들고 온다(`TONE`). */
export const KIND_KO: Record<AlarmKind, string> = {
  blocked: tx("alarm.kind.blocked"),
  rework: tx("alarm.kind.rework"),
  "session-failed": tx("alarm.kind.sessionFailed"),
  stale: tx("alarm.kind.stale"),
  review: tx("alarm.kind.review"),
  "runner-down": tx("alarm.kind.runnerDown"),
};
export const KIND_KEYS = Object.keys(KIND_KO) as AlarmKind[];

type ChipTone = "error" | "warning" | "info" | "muted";
/** 톤 셋 — 글리프 · 칩 톤. **색만으로 가르지 않는다**: 글리프와 글자가 함께 간다. */
export const TONE: Record<AlarmTone, { glyph: string; chip: ChipTone }> = {
  alert: { glyph: "▲", chip: "error" },
  warn: { glyph: "◷", chip: "warning" },
  info: { glyph: "●", chip: "info" },
};

export function kindText(kind: string): string {
  return (KIND_KO as Record<string, string>)[kind] ?? kind;
}
export function toneOf(tone: string): { glyph: string; chip: ChipTone } {
  return (TONE as Record<string, { glyph: string; chip: ChipTone }>)[tone] ?? { glyph: "?", chip: "muted" };
}

/** 알람을 누르면 갈 주소. 세션 실패는 주소가 아니라 겉창이라 `null` 이다 — 부르는 쪽이
 *  `openDock` 을 연다. 모르는 `go` 는 알람 화면에 머문다(갈 곳이 없는 알람은 없다). */
export function hrefOf(a: Alarm): string | null {
  switch (a.go) {
    case "session":
      return null;
    case "task":
      return a.taskId ? `#/board?open=${encodeURIComponent(a.taskId)}` : "#/alarms";
    case "tidy":
      return "#/tidy";
    case "servers":
      return a.runner ? `#/servers?s=${encodeURIComponent(a.runner)}` : "#/servers";
    default:
      return "#/alarms";
  }
}

/** 「누르면 →」 한 마디 — 줄 아래 meta 에 적는다. */
export function goText(a: Alarm): string {
  switch (a.go) {
    case "session":
      return tx("alarm.go.session");
    case "task":
      return tx("alarm.go.task");
    case "tidy":
      return tx("nav.tidy");
    case "servers":
      return tx("alarm.go.servers");
    default:
      return tx("alarm.go.none");
  }
}

/** 종이 새로 읽을 때를 알린다 — 읽음 처리 뒤 숫자를 바로 맞춘다(타이머 없이). */
export const ALARMS_CHANGED = "b3x:alarms-changed";
export function alarmsChanged() {
  dispatchEvent(new Event(ALARMS_CHANGED));
}
